import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as turf from "@turf/turf";
import { useNavigate } from "react-router-dom";
import MapboxCustomSearch from "./MapboxCustomSearch"; // Import the custom search component
import { Save } from 'lucide-react';
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

const MapboxExample = () => {
  const mapContainerRef = useRef();
  const mapRef = useRef();
  const drawRef = useRef();
  const navigate = useNavigate();
  const [roundedArea, setRoundedArea] = useState(null);
  const [polygonData, setPolygonData] = useState(null);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/satellite-streets-v11");
  const [mapInstance, setMapInstance] = useState(null);


  const mapStyles = [
    { label: "Satellite", value: "mapbox://styles/mapbox/satellite-streets-v11" },
    { label: "Streets", value: "mapbox://styles/mapbox/streets-v12" },
    { label: "Outdoors", value: "mapbox://styles/mapbox/outdoors-v12" },
    { label: "Light", value: "mapbox://styles/mapbox/light-v11" },
    { label: "Dark", value: "mapbox://styles/mapbox/dark-v11" },
  ];

  // Ensure no body/html constraints when map is mounted
  useEffect(() => {
    // Store original values
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyHeight = document.body.style.height;
    const originalRootHeight = document.getElementById('root')?.style.height;
    
    // Add class to body for CSS targeting
    document.body.classList.add('map-active');
    
    // Force clean styles IMMEDIATELY
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.height = '100vh';
    document.body.style.position = 'fixed';
    document.body.style.width = '100vw';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.height = '100vh';
    
    // Ensure #root doesn't constrain
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.style.height = '100vh';
      rootEl.style.overflow = 'hidden';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('map-active');
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.height = originalBodyHeight;
      document.body.style.position = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.documentElement.style.height = '';
      if (rootEl) {
        rootEl.style.height = originalRootHeight || '';
        rootEl.style.overflow = '';
      }
    };
  }, []);

  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center: [76.95390347, 11.01615852],
      zoom: 17,
    });

    setMapInstance(mapRef.current);

    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, trash: true },
      defaultMode: "draw_polygon",
    });

    mapRef.current.addControl(drawRef.current);

    mapRef.current.on("draw.create", updateArea);
    mapRef.current.on("draw.delete", () => {
      setRoundedArea(null);
      setPolygonData(null);
    });
    mapRef.current.on("draw.update", updateArea);

    // Force map to resize after DOM settles
    mapRef.current.on('load', () => {
      // Wait for next frame to ensure container has correct size
      requestAnimationFrame(() => {
        if (mapRef.current) {
          mapRef.current.resize();
        }
      });
    });

    return () => mapRef.current.remove();
  }, [mapStyle]);

  function updateArea() {
    const data = drawRef.current.getAll();
    if (data.features.length > 0) {
      const area = turf.area(data);
      setRoundedArea(Math.round(area * 100) / 100);
      setPolygonData(data.features[0].geometry);
    } else {
      setRoundedArea(null);
      setPolygonData(null);
      alert("Click the map to draw a polygon.");
    }
  }

  // Function to save the polygon and area
  const savePolygon = () => {
    if (polygonData) {
      const areaInSqMeters = turf.area({
        type: "FeatureCollection",
        features: [{ geometry: polygonData }],
      });

      // Pass polygon and area (in sq. meters) via navigate
      navigate("/sites", {
        state: {
          polygon: polygonData,
          area: Math.round(areaInSqMeters), // rounded square meters
        },
      });
    } else {
      alert("No polygon data to save.");
    }
  };


  const handleStyleChange = (e) => {
    setMapStyle(e.target.value);
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div 
      className="fullscreen-map-container"
      style={{ 
        height: "100vh", 
        width: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 0,
        padding: 0,
        overflow: "hidden",
        zIndex: 9999,
        transform: "translate3d(0, 0, 0)" // Force GPU rendering and override any transforms
      }}>
      {/* Hide MapboxDraw instruction banner */}
      <style>{`
        .mapbox-gl-draw_ctrl-draw-btn,
        .mapboxgl-ctrl-group button {
          position: relative;
        }
        .mapbox-gl-draw_ctrl-draw-btn::after,
        .mapbox-gl-draw_polygon::after,
        .mapboxgl-ctrl-group button::after {
          display: none !important;
        }
        /* Hide the central instruction overlay */
        .mapboxgl-canvas-container.mapboxgl-interactive {
          cursor: crosshair !important;
        }
      `}</style>
      <div ref={mapContainerRef} id="map" style={{ 
        height: "100vh",
        width: "100vw" 
      }}></div>

      {/* Area and Save Button */}
      <div
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          background: "linear-gradient(135deg, #4880ff 0%, #6366f1 100%)",
          padding: "16px 20px",
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(108, 142, 191, 0.3)",
          fontFamily: "'Public Sans', sans-serif",
          color: "white",
          minWidth: "280px"
        }}
      >
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "8px",
          marginBottom: "12px",
          fontSize: "16px",
          fontWeight: 600
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          Draw Site Boundary
        </div>
        {roundedArea !== null && (
          <p style={{ 
            margin: "0 0 12px 0", 
            fontSize: "15px",
            fontWeight: 500,
            background: "rgba(255,255,255,0.15)",
            padding: "8px 12px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}>
            <span style={{ fontSize: "18px" }}>⬡</span> Area: {polygonData ? turf.area({ type: "FeatureCollection", features: [{ geometry: polygonData }] }).toFixed(2) : 0} sq. meters
          </p>
        )}
        <button
          onClick={savePolygon}
          disabled={!polygonData}
          style={{
            backgroundColor: polygonData ? "white" : "rgba(255,255,255,0.3)",
            color: polygonData ? "#4880ff" : "rgba(255,255,255,0.6)",
            border: "none",
            padding: "10px 20px",
            borderRadius: "8px",
            cursor: polygonData ? "pointer" : "not-allowed",
            fontSize: "14px",
            fontWeight: 600,
            width: "100%",
            transition: "all 0.2s",
            boxShadow: polygonData ? "0 4px 12px rgba(0,0,0,0.1)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}
          onMouseOver={(e) => {
            if (polygonData) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.15)";
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = polygonData ? "0 4px 12px rgba(0,0,0,0.1)" : "none";
          }}
        >
          <Save size={18} />
          Save Polygon & Continue
        </button>
      </div>

      {/* Custom Search Box */}
      <MapboxCustomSearch map={mapInstance} />

      {/* Style Switcher */}
      <div
        style={{
          position: "absolute",
          top: 20,
          right: 80,
          background: "white",
          padding: "12px 16px",
          borderRadius: "12px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontFamily: "'Public Sans', sans-serif",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4880ff" strokeWidth="2">
          <path d="M12 2v6m0 4v10M6 8l-4 4 4 4m12-8l4 4-4 4"></path>
        </svg>
        <select
          value={mapStyle}
          onChange={handleStyleChange}
          style={{
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1.5px solid #e5e7eb",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            outline: "none",
            background: "white",
            color: "#1a2333"
          }}
        >
          {mapStyles.map((style) => (
            <option key={style.value} value={style.value}>
              {style.label}
            </option>
          ))}
        </select>
      </div>

      {/* Close Button */}
      <button
        onClick={handleClose}
        style={{
          backgroundColor: "#ef4444",
          color: "#fff",
          border: "none",
          padding: "12px",
          borderRadius: "12px",
          cursor: "pointer",
          fontSize: "20px",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "48px",
          height: "48px",
          transition: "all 0.2s",
          position: "absolute",
          top: 20,
          right: 20,
          boxShadow: "0 4px 16px rgba(239, 68, 68, 0.3)"
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = "#dc2626";
          e.target.style.transform = "scale(1.05)";
          e.target.style.boxShadow = "0 6px 20px rgba(239, 68, 68, 0.4)";
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = "#ef4444";
          e.target.style.transform = "scale(1)";
          e.target.style.boxShadow = "0 4px 16px rgba(239, 68, 68, 0.3)";
        }}
        title="Close"
      >
        ✕
      </button>
    </div>
  );
};

export default MapboxExample;
